import React from 'react';
import { resetForm, showToast, SystemTenantForm } from '@kineticdata/react';
import {
  generateFormButtons,
  FormError,
  SecretFieldGroup,
  ChangeSecretField,
  SecretField,
} from '@kineticdata/ui';
import { regRoute } from '../../router';
import { regSaga } from '../../saga';
import { call, cancel, delay, fork, put, takeEvery } from 'redux-saga/effects';
import { dispatch, regHandlers } from '../../store';
import { useParams } from 'react-router-dom';
import {
  DATABASE_ADAPTER_FIELDS,
  MssqlPropertyLayout,
  OraclePropertyLayout,
  PostgresPropertyLayout,
} from '../common/DatabaseAdapters';
import {
  fetchTenantDeploymentStatus,
  restartTenantDeployments,
} from '@kineticdata/react/src/apis/system';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { NavListItemLink } from '../common/NavListItemLink';
import { SpaceEditTaskEngine } from './SpaceEditTaskEngine';

const handleSave = () => () => {
  showToast(
    { content: `The space's task component was updated successfully.` },
    'success',
  );
};

regRoute('TENANT_EDIT_TASK_PAGE', '/spaces/edit/:spaceSlug/task/*', {
  formKey: ({ payload }) => `${payload?.params?.spaceSlug}-tenant-task`,
});

regSaga(
  takeEvery('LEAVE_TENANT_EDIT_TASK_PAGE', function* () {
    yield put({ type: 'CLEAR_TENANT_TASK_STATUS' });
    yield cancel(taskStatusPoller);
  }),
);

let taskStatusPoller;
function* pollTaskStatus(slug) {
  while (true) {
    const { deployment } = yield call(fetchTenantDeploymentStatus, {
      slug,
      deployment: 'task',
    });
    yield put({
      type: 'SET_TENANT_TASK_STATUS',
      payload: deployment?.status,
    });
    if (deployment?.status === 'Running') {
      yield cancel(taskStatusPoller);
    } else {
      yield delay(5000);
    }
  }
}

regSaga(
  takeEvery(
    'INIT_TENANT_TASK_STATUS',
    function* ({ payload: { slug, status } }) {
      yield put({
        type: 'SET_TENANT_TASK_STATUS',
        payload: status,
      });
      if (!!status && !['Running', 'Not Found'].includes(status)) {
        taskStatusPoller = yield fork(pollTaskStatus, slug);
      }
    },
  ),
);

regSaga(
  takeEvery('TENANT_RESTART_TASK', function* ({ payload: slug }) {
    yield call(restartTenantDeployments, { slug, deployment: 'task' });
    taskStatusPoller = yield fork(pollTaskStatus, slug);
  }),
);

regHandlers({
  SET_TENANT_TASK_STATUS: (state, { payload }) =>
    state.setIn(['page', 'state', 'tenantTaskStatus'], payload),
  CLEAR_TENANT_TASK_STATUS: state =>
    state.deleteIn(['page', 'state', 'tenantTaskStatus']),
});

const initTaskStatus = (slug, status) => {
  dispatch('INIT_TENANT_TASK_STATUS', { slug, status });
};

const handleTaskRestart = slug => () => {
  dispatch('TENANT_RESTART_TASK', slug);
};

const TaskStatusField = ({ slug }) => {
  const status = useSelector(state =>
    state.getIn(['page', 'state', 'tenantTaskStatus'], 'Loading'),
  );

  return (
    <div className="form-group">
      <label>Task Status</label>
      <div className="input-group input-group--map-field">
        <div
          className={classNames('form-control-plaintext rounded', {
            'animate-success-bg': status === 'Running',
            'bg-light': status === 'Loading',
            'bg-danger': status !== 'Running' && status !== 'Loading',
          })}
          style={{ maxWidth: '40vw' }}
        />
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleTaskRestart(slug)}
          disabled={status !== 'Running'}
        >
          Restart
        </button>
      </div>
      <div className="form-control-plaintext">
        <em className="fw-semibold">{status}</em>
      </div>
    </div>
  );
};

const FormLayout = ({
  fields,
  error,
  buttons,
  bindings,
  meta,
  formOptions: { slug },
}) => {
  const currentAdapter = bindings.values.get('task_databaseAdapter_type');
  const enabled = bindings.tenant?.getIn(['features', 'task']) === 'true';

  return (
    <form>
      {fields.get('task_feature')}
      {bindings.values.get('task_feature') && (
        <>
          {enabled && <TaskStatusField slug={slug} />}
          <div className="row">
            <div className={enabled ? 'col-md-4' : 'col-md-6'}>
              {fields.get('task_replicas')}
            </div>
            {!enabled && (
              <div className="col-md-6">{fields.get('task_imageToggle')}</div>
            )}
            <div className={enabled ? 'col-md-8' : 'col-md-12'}>
              {fields.get('task_image')}
            </div>
          </div>
          <SecretFieldGroup
            secretField={fields.get('task_authenticationSecret')}
            changeSecretField={fields.get('task_changeAuthenticationSecret')}
          />
          <div className="heading heading-sm">
            <h2 className="h3">Task Database</h2>
          </div>
          <div className="row">
            <div className="col-6">
              {fields.get('task_databaseAdapter_type')}
            </div>
            <div className="col-6">{fields.get('task_autoCreateDatabase')}</div>
          </div>
          {currentAdapter === 'mssql' ? (
            <MssqlPropertyLayout
              fields={fields}
              values={bindings.values}
              buildFieldName={name => `task_mssql_${name}`}
            />
          ) : currentAdapter === 'oracle' ? (
            <OraclePropertyLayout
              fields={fields}
              values={bindings.values}
              buildFieldName={name => `task_oracle_${name}`}
            />
          ) : currentAdapter === 'postgres' &&
            !bindings.values.get('task_autoCreateDatabase') ? (
            <PostgresPropertyLayout
              fields={fields}
              values={bindings.values}
              buildFieldName={name => `task_postgres_${name}`}
            />
          ) : null}
        </>
      )}
      {error}
      <div className="form-buttons">{buttons}</div>
    </form>
  );
};

export const SpaceEditTask = ({ handleDelete }) => {
  const { spaceSlug, subTab } = useParams();
  return (
    <SystemTenantForm
      formKey={`${spaceSlug}-tenant-task`}
      onSave={handleSave}
      component="task"
      slug={spaceSlug}
      components={{
        FormError,
        FormButtons: generateFormButtons({
          handleDelete,
          submitLabel: 'Update Space',
          includeReset: true,
        }),
        FormLayout,
      }}
      alterFields={{
        task_feature: {
          renderAttributes: { switch: true },
        },
        task_autoCreateDatabase: {
          renderAttributes: { wrapperClassName: 'pt-v32' },
        },
        task_replicas: {
          renderAttributes: { min: 0 },
        },
        task_imageToggle: {
          renderAttributes: { wrapperClassName: 'pt-v32' },
        },
        ...DATABASE_ADAPTER_FIELDS(spaceSlug),
        task_changeAuthenticationSecret: { component: ChangeSecretField },
        task_authenticationSecret: { component: SecretField },
      }}
      onLoad={() =>
        ({ tenant }) => {
          initTaskStatus(
            spaceSlug,
            tenant?.getIn(['task', 'deployment', 'status']),
          );
        }}
    >
      {({ form, initialized, bindings: { tenant } }) =>
        initialized &&
        (tenant.getIn(['features', 'task']) === 'false' ? (
          <div className="content-container">{form}</div>
        ) : (
          <>
            <ul className="nav nav-tabs">
              <NavListItemLink to={`/spaces/edit/${spaceSlug}/task`} end>
                Infrastructure
              </NavListItemLink>
              <NavListItemLink
                to={`/spaces/edit/${spaceSlug}/task/engine`}
                onClick={() => resetForm(`${spaceSlug}-tenant-task`)}
              >
                Engine
              </NavListItemLink>
            </ul>
            {subTab === 'engine' ? (
              <SpaceEditTaskEngine spaceSlug={spaceSlug} />
            ) : (
              <div className="content-container">{form}</div>
            )}
          </>
        ))
      }
    </SystemTenantForm>
  );
};
